import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, IconButton, Box, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

export default function ImageViewerModal({ open, src, title, onClose }) {
  const [scale, setScale] = useState(1);

  useEffect(() => {
    if (open) {
      setScale(1);
    }
  }, [open, src]);

  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev + 0.25, 4));
  };

  const handleZoomOut = () => {
    setScale((prev) => Math.max(prev - 0.25, 0.5));
  };

  const handleReset = () => {
    setScale(1);
  };

  const handleWheel = (e) => {
    if (e.deltaY < 0) {
      setScale((prev) => Math.min(prev + 0.15, 4));
    } else if (e.deltaY > 0) {
      setScale((prev) => Math.max(prev - 0.15, 0.5));
    }
  };

  if (!open || !src) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        style: {
          backgroundColor: '#111',
          color: '#fff',
          borderRadius: 12,
          overflow: 'hidden',
          minHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header controls bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justify: 'space-between',
          padding: '12px 24px',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          zIndex: 10,
        }}
      >
        <Typography variant="h6" style={{ color: '#fff', fontWeight: 'bold', fontSize: 16 }}>
          {title || 'Image Preview'}
        </Typography>

        {/* Zoom & Action buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={handleZoomOut}
            disabled={scale <= 0.5}
            style={{ color: '#fff', backgroundColor: 'rgba(255, 255, 255, 0.15)' }}
            size="small"
            title="Zoom Out (-)"
          >
            <RemoveIcon />
          </IconButton>

          <Typography variant="body2" style={{ color: '#fff', minWidth: 45, textAlign: 'center', fontWeight: 'bold' }}>
            {Math.round(scale * 100)}%
          </Typography>

          <IconButton
            onClick={handleZoomIn}
            disabled={scale >= 4}
            style={{ color: '#fff', backgroundColor: 'rgba(255, 255, 255, 0.15)' }}
            size="small"
            title="Zoom In (+)"
          >
            <AddIcon />
          </IconButton>

          <IconButton
            onClick={handleReset}
            style={{ color: '#fff', backgroundColor: 'rgba(255, 255, 255, 0.15)', marginLeft: 8 }}
            size="small"
            title="Reset Zoom"
          >
            <RestartAltIcon />
          </IconButton>

          <IconButton
            onClick={onClose}
            style={{ color: '#fff', backgroundColor: 'rgba(239, 68, 68, 0.8)', marginLeft: 16 }}
            size="small"
            title="Close"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Main Image Container */}
      <DialogContent
        onWheel={handleWheel}
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: '#000',
          overflow: 'hidden',
          padding: 20,
          flex: 1,
          cursor: scale > 1 ? 'grab' : 'default',
        }}
      >
        <img
          src={src}
          alt={title || 'Preview'}
          style={{
            maxHeight: '75vh',
            maxWidth: '100%',
            objectFit: 'contain',
            transform: `scale(${scale})`,
            transition: 'transform 0.15s ease-out',
            userSelect: 'none',
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
